import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useNotifications } from '../../hooks/useNotifications';
import LoadingSpinner from '../common/LoadingSpinner/LoadingSpinner';
import { Calendar, IdCard, Clipboard, ChartNoAxesColumn, MessageCircle, ChartLine, Zap, Search, Hourglass, Target, Flame, TriangleAlert, SquareCheckBig, Folder, Ruler, Download, Check, X, MessageCircleQuestionIcon, ChevronRight, ChevronDown } from 'lucide-react';
import { estadosDisponibles } from '@/config/constants';

const getStatusInfo = (status) => {
  return estadosDisponibles.find(e => e.id === status) ||
    { icon: <MessageCircleQuestionIcon />, color: 'gray', name: status };
}

// Funciones para acciones rápidas de documentos
const handleQuickApprove = async (document) => {
  try {
    const token = localStorage.getItem('token');
    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/managers/documents/${document._id}/review`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        status: 'approved',
        review_notes: 'Aprobado rápidamente por gestor'
      })
    });

    if (response.ok) {
      console.success(`✓ Documento "${document.required_document_name}" aprobado`);
      fetchDocuments();
    } else {
      console.warning('❌ Error aprobando documento');
    }
  } catch (err) {
    console.error('❌ Error de conexión');
  }
};

const handleQuickReject = async (document) => {
  const motivo = prompt('¿Motivo del rechazo?', 'Documento no cumple con los requisitos');
  if (!motivo) return;

  try {
    const token = localStorage.getItem('token');
    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/managers/documents/${document.id}/review`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        status: 'rejected',
        rejection_reason: motivo
      })
    });

    if (response.status === "approved") {
      console.success(`✗ Documento "${document.required_document_name}" rechazado`);
      fetchDocuments();
    } else {
      console.warning('❌ Error rechazando documento');
    }
  } catch (err) {
    console.error('❌ Error de conexión');
  }
};

const handleDownload = async (document) => {
  const token = localStorage.getItem('token');
  await fetch(`${process.env.REACT_APP_BACKEND_URL}/documents/${document._id}/download`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  }).then(async (res) => {
    if (!res.ok) throw new Error("No autorizado o archivo no encontrado");

    const blob = await res.blob();
    const fileURL = URL.createObjectURL(blob);
    window.open(fileURL, "_blank");
  })
    .catch((err) => console.error(err));
};

const AdvancedGestorPanel = () => {
  const { user } = useAuth();
  const { success, info, warning, error } = useNotifications();

  // Estados principales
  const [pendingDocs, setPendingDocs] = useState([]);
  const [allDocuments, setAllDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');

  // Estados para filtros avanzados
  const [busquedaCliente, setBusquedaCliente] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('todos');
  const [filtroUrgencia, setFiltroUrgencia] = useState('todos');
  const [fechaDesde, setFechaDesde] = useState('');

  // Estados para acciones en lote
  const [documentosSeleccionados, setDocumentosSeleccionados] = useState([]);
  const [modoSeleccion, setModoSeleccion] = useState(false);

  // Estados para métricas
  const [metricas, setMetricas] = useState(null);



  // Función para obtener métricas
  const fetchMetricas = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/managers/metrics`, {
        headers
      });

      if (response.ok) {
        const data = await response.json();
        setMetricas(data);
      } else {
        // Métricas simuladas enfocadas en trámites
        setMetricas({
          hoy: Math.floor(Math.random() * 5) + 1,
          esta_semana: Math.floor(Math.random() * 15) + 5,
          tiempo_promedio: (Math.random() * 3 + 1).toFixed(1),
          total_revisados: Math.floor(Math.random() * 50) + 20,
          pendientes: 0,
          documentos_totales: 0
        });
      }
    } catch (error) {
      console.error('Error fetching metrics:', error);
    }
  };

  const fetchDocuments = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };

      const [pendingRes, allRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/managers/pending-documents`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/admin/documents/all`, { headers })
      ]);

      if (pendingRes.ok) {
        const pendingData = await pendingRes.json();

        setPendingDocs(pendingData);

        if (metricas) {
          setMetricas(prev => ({
            ...prev,
            pendientes: tramitesUnicosPendientes.length,
            documentos_totales: pendingData.length
          }));
        }

        success(`${pendingData.length} documentos pendientes cargados`);
      }

      if (allRes.ok) {
        const allData = await allRes.json();

        setAllDocuments(allData.documents || []);
      }
    } catch (err) {
      error('❌ Error cargando documentos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
    fetchMetricas();

    // Auto-refresh con notificaciones
    const interval = setInterval(() => {
      fetchDocuments();
      info('🔄 Panel actualizado automáticamente');
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  // Verificar permisos
  if (user?.role !== 'gestor' && user?.role !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="text-6xl mb-4">🔒</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Acceso Denegado</h2>
          <p className="text-gray-600">Solo gestores y administradores pueden revisar documentos.</p>
        </div>
      </div>
    );
  }

  // Funciones para acciones en lote
  const toggleSeleccionDocumento = (docId) => {
    setDocumentosSeleccionados(prev =>
      prev.includes(docId)
        ? prev.filter(id => id !== docId)
        : [...prev, docId]
    );
  };

  const seleccionarTodos = () => {
    const todosPendientes = pendingDocs.filter(doc => doc.status === 'pending').map(doc => doc.id);
    setDocumentosSeleccionados(todosPendientes);
  };

  const limpiarSeleccion = () => {
    setDocumentosSeleccionados([]);
    info('🗑️ Selección limpiada');
  };

  const aprobarSeleccionados = async () => {
    if (documentosSeleccionados.length === 0) return;

    try {
      const token = localStorage.getItem('token');
      const promises = documentosSeleccionados.map(docId =>
        fetch(`${process.env.REACT_APP_BACKEND_URL}/managers/documents/${docId}/review`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            status: 'approved',
            review_notes: 'Aprobado en lote por gestor'
          })
        })
      );

      await Promise.all(promises);
      success(<SquareCheckBig /> + `${documentosSeleccionados.length} documentos aprobados en lote`);

      limpiarSeleccion();
      fetchDocuments();

    } catch (err) {
      warning('❌ Error en aprobación en lote');
    }
  };

  // Función para calcular urgencia
  const calcularUrgencia = (fechaCreacion) => {
    const ahora = new Date();
    const fecha = new Date(fechaCreacion);
    const diasEspera = Math.floor((ahora - fecha) / (1000 * 60 * 60 * 24));

    if (diasEspera > 7) return { nivel: 'urgente', color: 'red', dias: diasEspera, icon: <Flame /> };
    if (diasEspera > 3) return { nivel: 'media', color: 'yellow', dias: diasEspera, icon: '<TriangleAlert />' };
    return { nivel: 'normal', color: 'green', dias: diasEspera, icon: '🟢' };
  };

  if (loading) {
    return <LoadingSpinner message="Cargando panel de gestión avanzado..." />;
  }

  // Calcular número de trámites únicos en lugar de documentos
  const tramitesUnicosPendientes = [...new Set(pendingDocs.map(doc => doc.application_id))];
  const tramitesUnicosTotal = [...new Set(allDocuments.map(doc => doc.application_id))];

  const tabs = [
    { id: 'pending', name: `Trámites Pendientes (${tramitesUnicosPendientes.length})`, icon: <Hourglass /> },
    { id: 'all', name: `Todos los Trámites (${tramitesUnicosTotal.length})`, icon: <Clipboard /> }
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header mejorado */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900">
            Panel Gestor
          </h1>
          <p className="text-gray-600 mt-2 text-lg">
            Herramientas avanzadas para gestión eficiente de documentos
          </p>
        </div>

        {/* Métricas de rendimiento */}
        {metricas && (
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
            <MetricCard icon={<ChartNoAxesColumn />} title="Hoy" value={metricas.hoy} subtitle="revisados" color="blue" />
            <MetricCard icon={<ChartLine />} title="Esta Semana" value={metricas.esta_semana} subtitle="total" color="green" />
            <MetricCard icon={<Zap />} title="Tiempo Promedio" value={`${metricas.tiempo_promedio}d`} subtitle="días" color="yellow" />
            <MetricCard icon={<Target />} title="Total Histórico" value={metricas.total_revisados} subtitle="revisados" color="purple" />
            <MetricCard icon={<Hourglass />} title="Pendientes" value={metricas.pendientes || pendingDocs.length} subtitle="actuales" color="red" />
          </div>
        )}

        {/* Panel de filtros avanzados */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900">Filtros y Herramientas Avanzadas</h2>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setModoSeleccion(!modoSeleccion)}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${modoSeleccion ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
              >
                {modoSeleccion ? (<SquareCheckBig /> & 'Modo Selección ON') : '☑️ Activar Selección'}
              </button>
              {modoSeleccion && documentosSeleccionados.length > 0 && (
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                  {documentosSeleccionados.length} seleccionados
                </span>
              )}
            </div>
          </div>

          {/* Filtros en grid */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 flex row items-center">
                <Search /> Buscar Cliente
              </label>
              <input
                type="text"
                value={busquedaCliente}
                onChange={(e) => setBusquedaCliente(e.target.value)}
                placeholder="Nombre del cliente..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 flex row items-center">
                <ChartNoAxesColumn /> Estado
              </label>
              <select
                value={filtroEstado}
                onChange={(e) => setFiltroEstado(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="todos">Todos los estados</option>
                <option value="pending"><Hourglass /> Pendientes</option>
                <option value="approved"><SquareCheckBig /> Aprobados</option>
                <option value="rejected">❌ Rechazados</option>
                <option value="in_review">👀 En Revisión</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 flex row items-center">
                <Flame /> Urgencia
              </label>
              <select
                value={filtroUrgencia}
                onChange={(e) => setFiltroUrgencia(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="todos">Todas las urgencias</option>
                <option value="urgente"><Flame /> Urgente (+7 días)</option>
                <option value="media"><TriangleAlert /> Media (3-7 días)</option>
                <option value="normal">🟢 Normal (-3 días)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 flex row items-center"><Calendar /> Desde Fecha</label>
              <input
                type="date"
                value={fechaDesde}
                onChange={(e) => setFechaDesde(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Acciones en lote */}
          {modoSeleccion && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <button
                    onClick={seleccionarTodos}
                    className="text-sm text-blue-700 hover:text-blue-900 font-medium"
                  >
                    ☑️ Seleccionar Todos Pendientes
                  </button>
                  <button
                    onClick={limpiarSeleccion}
                    className="text-sm text-gray-600 hover:text-gray-800"
                  >
                    🗑️ Limpiar Selección
                  </button>
                  <span className="text-sm text-gray-600">
                    <ChartNoAxesColumn /> {documentosSeleccionados.length} documentos seleccionados
                  </span>
                </div>

                {documentosSeleccionados.length > 0 && (
                  <div className="flex space-x-3">
                    <button
                      onClick={aprobarSeleccionados}
                      className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 font-medium transition-colors"
                    >
                      ✓ Aprobar {documentosSeleccionados.length} Seleccionados
                    </button>
                    <button
                      onClick={() => warning('Funcionalidad de rechazo en lote próximamente')}
                      className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 font-medium transition-colors"
                    >
                      ✗ Rechazar Seleccionados
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="mb-8">
          <nav className="flex space-x-1 bg-gray-100 rounded-lg p-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 flex items-center justify-center px-4 py-2 text-sm font-medium rounded-md transition-colors ${activeTab === tab.id
                  ? 'bg-white text-blue-600 shadow'
                  : 'text-gray-500 hover:text-gray-700'
                  }`}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        {/* Contenido principal */}
        <div className="bg-white rounded-2xl shadow-lg">
          {activeTab === 'pending' && (
            <AdvancedPendingTab
              documents={pendingDocs}
              filtros={{
                busquedaCliente,
                filtroEstado,
                filtroUrgencia,
                fechaDesde
              }}
              modoSeleccion={modoSeleccion}
              documentosSeleccionados={documentosSeleccionados}
              onToggleSeleccion={toggleSeleccionDocumento}
              calcularUrgencia={calcularUrgencia}
            />
          )}

          {activeTab === 'all' && (
            <div className="p-8">
              <h2 className="text-2xl font-bold mb-4">Todos los Documentos</h2>
              <p className="text-gray-600">Vista de todos los documentos del sistema</p>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

const AdvancedPendingTab = ({
  documents,
  filtros,
  modoSeleccion,
  documentosSeleccionados,
  onToggleSeleccion,
  calcularUrgencia
}) => {
  // Aplicar todos los filtros
  let documentosFiltrados = documents.filter(doc => {
    // Filtro por búsqueda de cliente
    if (filtros.busquedaCliente && !doc.client_name?.toLowerCase().includes(filtros.busquedaCliente.toLowerCase())) {
      return false;
    }

    // Filtro por estado
    if (filtros.filtroEstado !== 'todos' && doc.status !== filtros.filtroEstado) {
      return false;
    }

    // Filtro por urgencia
    if (filtros.filtroUrgencia !== 'todos') {
      const urgencia = calcularUrgencia(doc.created_at);
      if (urgencia.nivel !== filtros.filtroUrgencia) {
        return false;
      }
    }

    // Filtro por fecha
    if (filtros.fechaDesde && new Date(doc.created_at) < new Date(filtros.fechaDesde)) {
      return false;
    }

    return true;
  });

  // Agrupar documentos filtrados
  const groupedDocuments = documentosFiltrados.reduce((groups, doc) => {
    const clientName = doc.client_name || 'Cliente Desconocido';
    const tramiteName = doc.tramite_name || 'Trámite Desconocido';
    const key = `${clientName}|||${tramiteName}|||${doc.application_id}`;

    if (!groups[key]) {
      groups[key] = {
        clientName,
        tramiteName,
        tramiteRequestId: doc.application_id,
        documents: []
      };
    }

    groups[key].documents.push(doc);
    return groups;
  }, {});

  // Ordenar grupos por urgencia máxima
  const groupedArray = Object.values(groupedDocuments).sort((a, b) => {
    const urgenciaA = Math.max(...a.documents.map(doc => calcularUrgencia(doc.created_at).dias));
    const urgenciaB = Math.max(...b.documents.map(doc => calcularUrgencia(doc.created_at).dias));
    return urgenciaB - urgenciaA;
  });

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">
          Trámites organizados por cliente y urgencia
        </h2>
        <div className="text-right">
          <div className="text-lg font-bold text-blue-600">{groupedArray.length}</div>
          <div className="text-sm text-gray-500">trámites únicos</div>
        </div>
      </div>

      {groupedArray.length > 0 ? (
        <div className="space-y-6">
          {groupedArray.map((group, index) => (
            <AdvancedClientGroup
              key={index}
              group={group}
              modoSeleccion={modoSeleccion}
              documentosSeleccionados={documentosSeleccionados}
              onToggleSeleccion={onToggleSeleccion}
              calcularUrgencia={calcularUrgencia}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-16">
          <div className="text-6xl mb-4 flex justify-center">
            {documentosFiltrados.length === 0 && documents.length > 0 ? <Search /> : <SquareCheckBig />}
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {documentosFiltrados.length === 0 && documents.length > 0
              ? 'No hay documentos que coincidan con los filtros'
              : 'No hay documentos pendientes'
            }
          </h3>
          <p className="text-gray-500">
            {documentosFiltrados.length === 0 && documents.length > 0
              ? 'Prueba ajustando los filtros de búsqueda'
              : 'Todos los documentos han sido revisados'
            }
          </p>
        </div>
      )}
    </div>
  );
};

/**
 * Grupo avanzado de cliente con urgencia y selección
 */
const AdvancedClientGroup = ({
  group,
  modoSeleccion,
  documentosSeleccionados,
  onToggleSeleccion,
  calcularUrgencia
}) => {
  const [expanded, setExpanded] = useState(true);

  // Calcular estadísticas y urgencia del grupo
  const totalDocs = group.documents.length;
  const pendingDocs = group.documents.filter(doc => doc.status === 'pending').length;
  const approvedDocs = group.documents.filter(doc => doc.status === 'approved').length;
  const rejectedDocs = group.documents.filter(doc => doc.status === 'rejected').length;

  const urgenciaMaxima = group.documents.reduce((max, doc) => {
    const urgencia = calcularUrgencia(doc.created_at);
    return urgencia.dias > max.dias ? urgencia : max;
  }, { dias: 0, nivel: 'normal', color: 'green', icon: '🟢' });

  const urgenciaColors = {
    urgente: 'bg-red-50 border-red-300',
    media: 'bg-yellow-50 border-yellow-300',
    normal: 'bg-green-50 border-green-300'
  };

  return (
    <div className={`border-2 rounded-xl overflow-hidden ${urgenciaColors[urgenciaMaxima.nivel]} shadow-sm`}>
      {/* Header con información del cliente y urgencia */}
      <div
        className="bg-white p-6 border-b border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="text-4xl">👤</div>
            <div>
              <div className="flex items-center space-x-3 mb-2">
                <h3 className="text-xl font-bold text-gray-900">
                  {group.clientName}
                </h3>
                {/* Badge de urgencia prominente */}
                <span className={`px-3 py-1 rounded-full text-xs font-bold ${urgenciaMaxima.nivel === 'urgente' ? 'bg-red-200 text-red-800 animate-pulse' :
                  urgenciaMaxima.nivel === 'media' ? 'bg-yellow-200 text-yellow-800' :
                    'bg-green-200 text-green-800'
                  }`}>
                  {urgenciaMaxima.icon} {urgenciaMaxima.nivel.toUpperCase()} - {urgenciaMaxima.dias} días
                </span>
              </div>

              <p className="text-gray-600 text-lg mb-1 flex row items-center">
                <Clipboard className='mr-1' />
                <span className="font-medium">
                  {group.tramiteName}
                </span>
              </p>
              <p className="text-sm text-gray-500 flex row items-center">
                <IdCard className='mr-1' />
                {group.tramiteRequestId ? group.tramiteRequestId : 'N/A'}
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-4">
            {/* Estadísticas rápidas */}
            <div className="flex space-x-2 text-sm">
              {pendingDocs > 0 && (
                <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full font-medium flex row items-center">
                  <Hourglass className='mr-1' /> {pendingDocs}
                </span>
              )}
              {approvedDocs > 0 && (
                <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full font-medium">
                  <SquareCheckBig /> {approvedDocs}
                </span>
              )}
              {rejectedDocs > 0 && (
                <span className="bg-red-100 text-red-800 px-2 py-1 rounded-full font-medium">
                  ❌ {rejectedDocs}
                </span>
              )}
            </div>

            {/* Navegación rápida */}
            <div className="flex space-x-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  window.location.href = `/applications/${group.tramiteRequestId}`;
                }}
                className="bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                title="Ver solicitud completa"
              >
                Ver Solicitud
              </button>
            </div>

            {/* Indicador expandir */}
            <div className="text-2xl text-gray-400">
              {expanded ? <ChevronDown /> : <ChevronRight />}
            </div>
          </div>
        </div>
      </div>

      {/* Lista de documentos */}
      {expanded && (
        <div className="p-6 bg-white">
          <h4 className="text-lg font-semibold text-gray-800 mb-4">
            Documentos ({totalDocs}):
          </h4>

          <div className="space-y-3">
            {group.documents.map((doc) => (
              <AdvancedDocumentCard
                key={doc.id}
                document={doc}
                modoSeleccion={modoSeleccion}
                isSelected={documentosSeleccionados.includes(doc.id)}
                onToggleSeleccion={onToggleSeleccion}
                urgencia={calcularUrgencia(doc.created_at)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Tarjeta avanzada de documento con selección y urgencia
 */
const AdvancedDocumentCard = ({
  document,
  modoSeleccion,
  isSelected,
  onToggleSeleccion,
  urgencia
}) => {

  const statusInfo = getStatusInfo(document.status);

  return (
    <div className={`border rounded-lg p-4 transition-all ${isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
      }`}>
      <div className="flex items-start justify-between">
        <div className="flex items-start space-x-3">
          {/* Checkbox para selección */}
          {modoSeleccion && (
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => onToggleSeleccion(document.id)}
              className="mt-1 w-4 h-4 text-blue-600"
              disabled={document.status !== 'pending'}
            />
          )}

          <div className="flex-1">
            <div className="flex items-center space-x-2 mb-2">
              <span className="text-xl">{statusInfo.icon}</span>
              <h5 className="font-medium text-gray-900">
                {document.required_document_name}
              </h5>
              {/* Badge de urgencia por documento */}
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${urgencia.nivel === 'urgente' ? 'bg-red-200 text-red-800' :
                urgencia.nivel === 'media' ? 'bg-yellow-200 text-yellow-800' :
                  'bg-green-200 text-green-800'
                }`}>
                {urgencia.icon} {urgencia.dias} d
              </span>
            </div>

            <div className="text-sm text-gray-600 space-y-1">
              <div className='flex row items-center'><Folder className='mr-1' />{document.file_path}</div>
              <div className='flex row items-center'><Ruler className='mr-1' />{(document.file_size / 1024 / 1024).toFixed(2)} MB</div>
              <div className='flex row items-center'><Calendar className='mr-1' />{new Date(document.created_at).toLocaleDateString()}</div>
            </div>
          </div>
        </div>

        {/* Acciones rápidas con íconos que contrasten */}
        <div className="flex space-x-2">
          {document.status === 'pending' && (
            <>
              <button
                onClick={() => handleQuickApprove(document)}
                className="bg-green-600 text-white px-3 py-1 rounded text-xs hover:bg-green-700"
                title="Aprobar rápido"
              >
                <Check />
              </button>
              <button
                onClick={() => handleQuickReject(document)}
                className="bg-red-600 text-white px-3 py-1 rounded text-xs hover:bg-red-700"
                title="Rechazar rápido"
              >
                <X />
              </button>
            </>
          )}
          <button
            onClick={() => handleDownload(document)}
            className="bg-blue-600 text-white px-3 py-1 rounded text-xs hover:bg-blue-700"
            title="Descargar documento"
          >
            <Download />
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Tarjeta de métrica
 */
const MetricCard = ({ icon, title, value, subtitle, color }) => {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600 border-blue-200',
    green: 'bg-green-50 text-green-600 border-green-200',
    yellow: 'bg-yellow-50 text-yellow-600 border-yellow-200',
    purple: 'bg-purple-50 text-purple-600 border-purple-200',
    red: 'bg-red-50 text-red-600 border-red-200'
  };

  return (
    <div className={`p-4 rounded-xl border-2 ${colorClasses[color]} text-center transform hover:scale-105 transition-transform cursor-pointer`}>
      <div className="text-3xl mb-2">{icon}</div>
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-sm font-medium opacity-80">{title}</div>
      <div className="text-xs opacity-60">{subtitle}</div>
    </div>
  );
};

export default AdvancedGestorPanel;